import React from 'react';

import {
  ClockIcon,
  ListIcon,
  UsersIcon,
  SlidersIcon,
  LocationCrosshairIcon,
  FileIcon,
  TechServerIcon,
  UserCheckIcon,
  DollarSignIcon,
  ViewGridIcon,
  VideoOnIcon,
  DetectionIcon,
  ChartBarVariantIcon,
  LocationGlobeIcon,
} from '@vlabs/icons';
import {
  LanguageButton, NavbarItem, NavbarPanel, NavbarPanelFooter, NavbarPanelHeader,
  NavbarPanelSection, NavbarPanelViewer, NavbarTypeToggleButton, ThemeButton, ViewerAuthButton,
} from '@vlabs/monokit/shared/ui/navbar-panel';
import { WhiteLabelBrand } from '@vlabs/monokit/shared/ui/white-label-brand';
import { NavbarLinkWithPermissions } from '@vlabs/shared/components/navbar-link-with-permissions/NavbarLinkWithPermissions';
import { permissions } from '@vlabs/shared/config';
import { selectAppServiceState, selectIsAppFeatureAvailable } from '@vlabs/shared/selectors/appSelectors';
import i18next from 'i18next';
import { useDispatch, useSelector } from 'react-redux';

import { selectIsAuthenticated, selectAccountModel } from '@vlapps/luna/src/features/auth/selectors';
import { logout } from '@vlapps/luna/src/features/auth/store';

import { routes } from '@vlapps/luna/src/app/routes';

import { ns, useNavigationTranslation } from './i18n';

// Опции языков формируются из build-time флагов локалей (__LOCALE_*):
// каждая опция добавляется в массив, только если её локаль включена в сборку.
const languageOptions = [
  ...(__LOCALE_RU__ ? [{
    value: 'ru',
    icon: <LocationGlobeIcon />,
    get label() {
      return i18next.t('ru', { ns });
    },
  }] : []),
  ...(__LOCALE_EN__ ? [{
    value: 'en',
    icon: <LocationGlobeIcon />,
    get label() {
      return i18next.t('en', { ns });
    },
  }] : []),
];

function LunaNavbar(): React.JSX.Element | null {
  const { t } = useNavigationTranslation();
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const account = useSelector(selectAccountModel);
  const lunaPlatformIsAvailable = useSelector((state) => selectAppServiceState(state, 'lunaPlatform')) === 'loaded';
  const lunaStreamsIsAvailable = useSelector((state) => selectAppServiceState(state, 'lunaStreams')) === 'loaded';
  const isFaceQualityAvailable = useSelector((state) => selectIsAppFeatureAvailable(state, 'estimations.face_quality'));

  if (!isAuthenticated) return null;

  return (
    <NavbarPanel
      footer={(
        <NavbarPanelFooter
          viewer={(
            <NavbarPanelViewer
              name={account?.name}
              role={account?.account_type}
            />
          )}
        >
          <ThemeButton />
          {import.meta.env.MODE !== 'whitelabel' && (
            <LanguageButton languageOptions={languageOptions} />
          )}
          <ViewerAuthButton
            isAuthenticated={isAuthenticated}
            onLogout={() => {
              logout(dispatch);
            }}
          />
        </NavbarPanelFooter>
      )}
      header={(
        <NavbarPanelHeader
          logo={<WhiteLabelBrand />}
          toggleButton={<NavbarTypeToggleButton />}
        />
      )}
      sections={[
        <NavbarPanelSection
          key="events_and_video"
          name={t('events_and_video')}
        >
          {!!lunaPlatformIsAvailable && (
            <NavbarLinkWithPermissions
              data-testid="latest_events"
              icon={<ClockIcon />}
              name={t('latest_events')}
              permissions={{ rules: [permissions.event.view] }}
              to={routes.events.latest}
            />
          )}
          {!!lunaPlatformIsAvailable && (
            <NavbarLinkWithPermissions
              data-testid="event_archive"
              icon={<ListIcon />}
              name={t('event_archive')}
              permissions={{ rules: [permissions.event.view] }}
              to={routes.events.archive}
            />
          )}
          {!!lunaPlatformIsAvailable && (
            <NavbarLinkWithPermissions
              data-testid="search"
              icon={<LocationCrosshairIcon />}
              name={t('search')}
              permissions={{ rules: [permissions.event.matching, permissions.face.matching] }}
              to={routes.search}
            />
          )}
          {!!lunaPlatformIsAvailable && !!isFaceQualityAvailable && (
            <NavbarLinkWithPermissions
              data-testid="checks"
              icon={<DetectionIcon />}
              name={t('checks')}
              permissions={{
                rules: [permissions.resources.iso, permissions.resources.sdk, permissions.emit_events.allowed],
              }}
              to={routes.checks.read}
            />
          )}
          {!!lunaPlatformIsAvailable && (
            <NavbarItem
              data-testid="event_statistic"
              icon={<ChartBarVariantIcon />}
              name={t('event_statistic')}
              to={routes.events.statistic.all}
            />
          )}
        </NavbarPanelSection>,
        <NavbarPanelSection
          key="settings_and_catalogs"
          name={t('settings_and_catalogs')}
        >
          {!!lunaPlatformIsAvailable && (
            <NavbarLinkWithPermissions
              data-testid="faces"
              icon={<UsersIcon />}
              name={t('faces')}
              permissions={{ rules: [permissions.face.view] }}
              to={routes.faces.all}
            />
          )}
          {!!lunaPlatformIsAvailable && (
            <NavbarLinkWithPermissions
              data-testid="lists"
              icon={<ListIcon />}
              name={t('lists')}
              permissions={{ rules: [permissions.list.view] }}
              to={routes.lists.all}
            />
          )}
          {!!(lunaStreamsIsAvailable) && (
            <NavbarLinkWithPermissions
              data-testid="fs_sources"
              icon={<VideoOnIcon />}
              name={t('fs_sources')}
              permissions={{ rules: [permissions.source.view] }}
              to={routes.sources.main}
            />
          )}
          {!!lunaPlatformIsAvailable && (
            <NavbarLinkWithPermissions
              data-testid="handlers"
              icon={<SlidersIcon />}
              name={t('handlers')}
              permissions={{ rules: [permissions.handler.view] }}
              to={routes.handlers.all}
            />
          )}
        </NavbarPanelSection>,
        <NavbarPanelSection
          key="administration"
          name={t('administration')}
        >
          {!!lunaPlatformIsAvailable && (
            <NavbarLinkWithPermissions
              data-testid="tasks"
              icon={<FileIcon />}
              name={t('tasks')}
              permissions={{ rules: [permissions.task.view] }}
              to={routes.tasks.all}
            />
          )}
          {!!lunaPlatformIsAvailable && (
            <NavbarLinkWithPermissions
              data-testid="accounts"
              icon={<UsersIcon />}
              name={t('accounts')}
              permissions={{ rules: [permissions.account.view] }}
              to={routes.accounts.all}
            />
          )}
          <NavbarItem
            data-testid="monitoring"
            icon={<TechServerIcon />}
            name={t('monitoring')}
            to={routes.app.info}
          />
          {!!lunaPlatformIsAvailable && (
            <NavbarItem
              data-testid="features"
              icon={<DollarSignIcon />}
              name={t('features')}
              to={routes.app.features}
            />
          )}
        </NavbarPanelSection>,
        <NavbarPanelSection
          key="other"
          name={t('other')}
        >
          {!!lunaPlatformIsAvailable && (
            <NavbarLinkWithPermissions
              data-testid="verifiers"
              icon={<UserCheckIcon />}
              name={t('verifiers')}
              permissions={{ rules: [permissions.verifier.view] }}
              to={routes.verifiers.all}
            />
          )}
          {!!lunaPlatformIsAvailable && (
            <NavbarItem
              data-testid="plugins"
              icon={<ViewGridIcon />}
              name={t('plugins')}
              to={routes.app.plugins}
            />
          )}
        </NavbarPanelSection>,
      ]}
    />
  );
}

export { LunaNavbar };
